import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Label, Form, FormGroup, Row, Col } from "reactstrap";
import { createAirportTerminal, updateAirportTerminal } from "../../../Services/ServiceCity.service";
import { showAlert } from "../../../Services/Shared.service";

const AirportForm = ({ cityId, editingTerminal, toggleModal, onAirportSaved, fetchAirports }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      terminal_name: "",
      terminal_short_name: "",
      kml: "",
      parking_charge: "",
      gmr: false,
      lat: "",
      lng: "",
    },
  });

  useEffect(() => {
    if (editingTerminal) {
      const termName = editingTerminal.terminalName || "";
      const termShortName = editingTerminal.terminalShortName || "";
      const pkgCharge = editingTerminal.parkingCharge !== undefined ? editingTerminal.parkingCharge : "";
      const specReporting = editingTerminal.specialReportingType || "";
      const latitudeVal = editingTerminal.lat !== undefined ? editingTerminal.lat : "";
      const longitudeVal =editingTerminal.lng !== undefined ? editingTerminal.lng : "";
      reset({
        terminal_name: termName,
        terminal_short_name: termShortName,
        kml: "",
        parking_charge: pkgCharge,
        gmr: specReporting === "GMR",
        lat: latitudeVal,
        lng: longitudeVal,
      });

      if (editingTerminal.kml) {
        try {
          const fileName = `${termName || 'Existing_Airport'}.kml`;
          const file = new File([editingTerminal.kml], fileName, { type: 'application/vnd.google-earth.kml+xml' });
          const dt = new DataTransfer();
          dt.items.add(file);
          setValue("kml", dt.files);
          setTimeout(() => {
            const fileInput = document.getElementById("kml-input-airport");
            if (fileInput) fileInput.files = dt.files;
          }, 0);
        } catch (e) {
          console.error("Error setting file input", e);
        }
      }
    } else {
      reset({
        terminal_name: "",
        terminal_short_name: "",
        kml: "",
        parking_charge: "",
        gmr: false,
        lat: "",
        lng: "",
      });
    }
  }, [editingTerminal, reset, setValue]);

  const onSubmit = async (formValues) => {
    setIsSubmitting(true);
    const termName = editingTerminal ? (editingTerminal.terminalName) : formValues.terminal_name;
    const termShortName = editingTerminal ? (editingTerminal.terminalShortName) : formValues.terminal_short_name;
    
    const requestPayload = {
      terminalName: termName,
      terminalShortName: termShortName,
      parkingCharge: parseFloat(formValues.parking_charge),
      specialReportingType: formValues.gmr ? "GMR" : "None",
      lat: parseFloat(formValues.lat),
      lng: parseFloat(formValues.lng),
    };
    const requestBlob = new Blob([JSON.stringify(requestPayload)], { type: 'application/json' });
    
    const formData = new FormData();
    formData.append("request", requestBlob);

    if (formValues.kml && formValues.kml.length > 0 && typeof formValues.kml !== "string") {
      formData.append("kmlFile", formValues.kml[0]);
    }

    try {
      const idToUpdate = editingTerminal ? (editingTerminal.terminalId) : null;
      if (editingTerminal && idToUpdate) {
        const res = await updateAirportTerminal(cityId, idToUpdate, formData);
        const updatedTerminal = res?.data?.data || {
          ...editingTerminal,
          terminalName: termName,
          terminalShortName: termShortName,
          parkingCharge: parseFloat(formValues.parking_charge),
          specialReportingType: formValues.gmr ? "GMR" : "None",
          lat: parseFloat(formValues.lat),
          lng: parseFloat(formValues.lng),
          terminalId: idToUpdate,
        };
        onAirportSaved(updatedTerminal, true);
        showAlert("success", "Airport terminal updated successfully");
      } else {
        await createAirportTerminal(cityId, formData);
        fetchAirports(); // Required to retrieve the database-generated terminalId for the new terminal
        showAlert("success", "Airport terminal created successfully");
      }
      toggleModal();
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Something went wrong";
      showAlert("error", errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Terminal Name <span style={{ color: "red" }}>*</span></Label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. Indira Gandhi Airport T1"
              disabled={!!editingTerminal}
              {...register("terminal_name", {
                required: "Terminal Name is required",
                onChange: (e) => setValue("terminal_name", e.target.value.trimStart()),
              })}
            />
            {errors.terminal_name && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.terminal_name.message}</span>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Terminal Short Name <span style={{ color: "red" }}>*</span></Label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. IGI-T1"
              disabled={!!editingTerminal}
              {...register("terminal_short_name", {
                required: "Terminal Short Name is required",
                onChange: (e) => setValue("terminal_short_name", e.target.value.trimStart()),
              })}
            />
            {errors.terminal_short_name && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.terminal_short_name.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Parking Charge <span style={{ color: "red" }}>*</span></Label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              placeholder="e.g. 150.00"
              {...register("parking_charge", {
                required: "Parking Charge is required",
                min: { value: 0, message: "Parking Charge cannot be negative" },
              })}
            />
            {errors.parking_charge && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.parking_charge.message}</span>
            )}
          </FormGroup>
        </Col>

        <Col md="6" className="d-flex align-items-center" style={{ marginTop: "20px" }}>
          <FormGroup className="d-flex align-items-center mb-0" style={{ gap: "10px" }}>
            <input
              type="checkbox"
              id="gmr"
              style={{
                width: "20px",
                height: "20px",
                cursor: "pointer",
                accentColor: "#39B54A",
              }}
              {...register("gmr")}
            />
            <Label for="gmr" className="mb-0" style={{ cursor: "pointer", fontWeight: "600", fontSize: "14px" }}>
              GMR (Special Reporting Type)
            </Label>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Latitude <span style={{ color: "red" }}>*</span></Label>
            <input
              type="number"
              step="any"
              className="form-control"
              placeholder="e.g. 28.5562"
              {...register("lat", {
                required: "Latitude is required",
                validate: {
                  validRange: (v) =>
                    (!isNaN(parseFloat(v)) && parseFloat(v) >= -90 && parseFloat(v) <= 90) ||
                    "Latitude must be between -90 and 90",
                },
              })}
            />
            {errors.lat && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.lat.message}</span>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>Longitude <span style={{ color: "red" }}>*</span></Label>
            <input
              type="number"
              step="any"
              className="form-control"
              placeholder="e.g. 77.1000"
              {...register("lng", {
                required: "Longitude is required",
                validate: {
                  validRange: (v) =>
                    (!isNaN(parseFloat(v)) && parseFloat(v) >= -180 && parseFloat(v) <= 180) ||
                    "Longitude must be between -180 and 180",
                },
              })}
            />
            {errors.lng && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.lng.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="12">
          <FormGroup>
            <Label style={{ fontWeight: "600" }}>KML Boundary File {!editingTerminal && <span style={{ color: "red" }}>*</span>}</Label>
            {editingTerminal && <div style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Upload a new file to replace the existing KML boundary.</div>}
            <input
              id="kml-input-airport"
              type="file"
              accept=".kml,.xml"
              className="form-control"
              {...register("kml", {
                required: editingTerminal ? false : "KML boundary file is required",
              })}
            />
            {errors.kml && (
              <span style={{ color: "red", fontSize: "12px" }}>{errors.kml.message}</span>
            )}
          </FormGroup>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2" style={{ marginTop: "20px" }}>
        <button
          type="button"
          className="btn btn-light"
          onClick={toggleModal}
          disabled={isSubmitting}
          style={{ textTransform: "none" }}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn"
          disabled={isSubmitting}
          style={{
            backgroundColor: "rgba(57, 181, 74, 1)",
            color: "white",
            textTransform: "none",
            fontWeight: "600",
          }}
        >
          {isSubmitting ? "Saving..." : editingTerminal ? "Update" : "Create"}
        </button>
      </div>
    </Form>
  );
};

export default AirportForm;
